import React from 'react';

/**
 * MiniDonut — donut chart en SVG puro, sin librerías externas.
 *
 * Props:
 *  - data: [{label, value, color}]  (colores HEX o vars CSS)
 *  - size: número (lado del SVG cuadrado, default 140)
 *  - centerLabel: string opcional (texto grande en el centro)
 *  - centerSubLabel: string opcional (texto pequeño debajo)
 */
export default function MiniDonut({
  data = [],
  size = 140,
  centerLabel,
  centerSubLabel,
}) {
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  const radius = size / 2 - 12;
  const cx = size / 2;
  const cy = size / 2;
  const strokeWidth = 14;

  // Si total=0, pintar un círculo gris de fondo
  if (total === 0) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={cx} cy={cy} r={radius}
          fill="none"
          stroke="rgb(241 245 249)"
          strokeWidth={strokeWidth}
        />
        {centerLabel && (
          <text
            x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
            className="text-2xl font-medium fill-slate-400"
          >
            {centerLabel}
          </text>
        )}
      </svg>
    );
  }

  // Calcular arcos
  let acumulado = 0;
  const arcos = data
    .filter((d) => d.value > 0)
    .map((d) => {
      const startAngle = (acumulado / total) * 360;
      acumulado += d.value;
      const endAngle = (acumulado / total) * 360;
      return { ...d, startAngle, endAngle };
    });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Fondo */}
      <circle
        cx={cx} cy={cy} r={radius}
        fill="none"
        stroke="rgb(241 245 249)"
        strokeWidth={strokeWidth}
      />
      {arcos.map((a, i) => (
        <path
          key={i}
          d={arcPath(cx, cy, radius, a.startAngle, a.endAngle)}
          fill="none"
          stroke={a.color}
          strokeWidth={strokeWidth}
          strokeLinecap="butt"
        />
      ))}
      {centerLabel && (
        <>
          <text
            x={cx}
            y={centerSubLabel ? cy - 8 : cy}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ fontSize: size * 0.22, fontWeight: 500 }}
            className="fill-slate-900 tabular-nums"
          >
            {centerLabel}
          </text>
          {centerSubLabel && (
            <text
              x={cx} y={cy + 16}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ fontSize: 10 }}
              className="fill-slate-500 uppercase tracking-wider"
            >
              {centerSubLabel}
            </text>
          )}
        </>
      )}
    </svg>
  );
}

function arcPath(cx, cy, r, startDeg, endDeg) {
  // Convertir grados a radianes, con offset de -90° para empezar arriba
  const start = (startDeg - 90) * (Math.PI / 180);
  const end = (endDeg - 90) * (Math.PI / 180);
  const x1 = cx + r * Math.cos(start);
  const y1 = cy + r * Math.sin(start);
  const x2 = cx + r * Math.cos(end);
  const y2 = cy + r * Math.sin(end);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  // Pequeño hack para que un único arco al 100% se cierre bien
  if (endDeg - startDeg >= 359.99) {
    return `
      M ${cx} ${cy - r}
      A ${r} ${r} 0 1 1 ${cx - 0.01} ${cy - r}
    `;
  }
  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
}
